import { KeywordHubLinks } from "@/components/site/KeywordHubLinks";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Play, Youtube, Headphones, Search, X } from "lucide-react";
import { episodes } from "@/lib/podcast-episodes";
import { useState, useMemo, useEffect } from "react";
import { cn } from "@/lib/utils";

const TOPICS = [
  { label: "All", keywords: [] as string[] },
  { label: "Finance", keywords: ["finance", "wealth", "capital", "invest", "banking", "fintech", "broking", "credit", "family office", "billionaire"] },
  { label: "E-commerce", keywords: ["e-commerce", "ecommerce", "quick commerce", "delivery", "supply chain", "inventory", "online retail", "Zomato", "Blinkit", "Nykaa"] },
  { label: "Consumer & Brand", keywords: ["consumer", "brand", "loyalty", "marketing", "D2C", "product", "customer", "boAt", "Mamaearth", "beauty"] },
  { label: "Hospitality", keywords: ["hospitality", "hotel", "travel", "OYO"] },
  { label: "Leadership", keywords: ["leadership", "hiring", "CEO", "founder", "management", "team", "culture", "decision"] },
];

function episodeSearchText(ep: (typeof episodes)[number]): string {
  return [ep.t, ep.g, ep.summary, ...ep.takeaways].join(" ").toLowerCase();
}

function matchesTopic(ep: (typeof episodes)[number], topicLabel: string): boolean {
  if (topicLabel === "All") return true;
  const topic = TOPICS.find((t) => t.label === topicLabel);
  if (!topic) return true;
  const text = episodeSearchText(ep);
  return topic.keywords.some((k) => text.includes(k.toLowerCase()));
}

function matchesQuery(ep: (typeof episodes)[number], query: string): boolean {
  if (!query.trim()) return true;
  const text = episodeSearchText(ep);
  return text.includes(query.toLowerCase().trim());
}


export const Route = createFileRoute("/podcast/")({
  head: () => ({
    meta: [
      { title: "Raj Shamani Podcast — Figuring Out: All Episodes", key: "title" },
      { name: "description", content: "Watch every Raj Shamani podcast episode of Figuring Out — summaries, key takeaways and transcripts with India's top founders, investors and leaders.", key: "description" },
      { name: "geo.region", content: "IN", key: "geo.region" },
      { name: "geo.placename", content: "Mumbai, India", key: "geo.placename" },
      { property: "og:title", content: "Raj Shamani Podcast — Figuring Out: All Episodes", key: "og:title" },
      { property: "og:description", content: "Watch every Raj Shamani podcast episode of Figuring Out — summaries, key takeaways and transcripts with India's top founders, investors and leaders.", key: "og:description" },
      { property: "og:url", content: "https://rajshamani.life/podcast", key: "og:url" },
      { property: "og:type", content: "website", key: "og:type" },
      { property: "og:site_name", content: "Raj Shamani", key: "og:site_name" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { property: "og:image", content: "https://rajshamani.life/__l5e/assets-v1/41ab8a52-52fd-4f68-937a-5f4dec00c4d5/og-podcast.jpg", key: "og:image" },
      { property: "og:image:alt", content: "Figuring Out — the Raj Shamani podcast", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "twitter:title", content: "Raj Shamani Podcast — Figuring Out: All Episodes", key: "twitter:title" },
      { name: "twitter:description", content: "Watch every Raj Shamani podcast episode of Figuring Out — summaries, key takeaways and transcripts with India's top founders, investors and leaders.", key: "twitter:description" },
      { name: "twitter:image", content: "https://rajshamani.life/__l5e/assets-v1/41ab8a52-52fd-4f68-937a-5f4dec00c4d5/og-podcast.jpg", key: "twitter:image" },
    ],
    links: [{ rel: "canonical", href: "https://rajshamani.life/podcast" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "PodcastSeries",
          "@id": "https://rajshamani.life/podcast#series",
          name: "Figuring Out",
          alternateName: "Figuring Out with Raj Shamani",
          url: "https://rajshamani.life/podcast",
          description:
            "Long-form, unfiltered conversations with the founders, leaders, and thinkers shaping India's next decade. Hosted by Raj Shamani.",
          inLanguage: "en",
          image:
            "https://rajshamani.life/__l5e/assets-v1/41ab8a52-52fd-4f68-937a-5f4dec00c4d5/og-podcast.jpg",
          author: {
            "@type": "Person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
            sameAs: [
              "https://youtube.com/@RajShamani",
              "https://instagram.com/rajshamani",
              "https://linkedin.com/in/rajshamani",
            ],
          },
          creator: { "@type": "Person", name: "Raj Shamani", url: "https://rajshamani.life/about" },
          publisher: {
            "@type": "Organization",
            name: "Figuring Out Media",
            url: "https://rajshamani.life/",
          },
          webFeed: "https://youtube.com/@RajShamani",
          sameAs: [
            "https://youtube.com/@RajShamani",
            "https://open.spotify.com",
          ],
          genre: ["Business", "Entrepreneurship", "Interview"],
          numberOfEpisodes: episodes.length,
          hasPart: episodes.map((ep) => ({
            "@type": "PodcastEpisode",
            "@id": `https://rajshamani.life/podcast/${ep.slug}`,
            url: `https://rajshamani.life/podcast/${ep.slug}`,
            name: ep.t,
            episodeNumber: parseInt(ep.n.replace(/^EP\s*/, ""), 10),
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": episodes.map((ep) => {
            const m = ep.d.match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);
            const hours = m?.[1] ? parseInt(m[1], 10) : 0;
            const mins = m?.[2] ? parseInt(m[2], 10) : 0;
            const duration =
              hours || mins
                ? `PT${hours ? `${hours}H` : ""}${mins ? `${mins}M` : ""}`
                : "PT0M";
            const epUrl = `https://rajshamani.life/podcast/${ep.slug}`;
            return {
              "@type": "PodcastEpisode",
              "@id": epUrl,
              name: ep.t,
              episodeNumber: parseInt(ep.n.replace(/^EP\s*/, ""), 10),
              description: `${ep.t} — with ${ep.g} on Figuring Out with Raj Shamani.`,
              datePublished: ep.publishedAt,
              duration,
              url: epUrl,
              inLanguage: "en",
              actor: { "@type": "Person", name: ep.g },
              author: { "@type": "Person", name: "Raj Shamani" },
              partOfSeries: {
                "@type": "PodcastSeries",
                "@id": "https://rajshamani.life/podcast#series",
                name: "Figuring Out",
                url: "https://rajshamani.life/podcast",
              },
              associatedMedia: {
                "@type": "MediaObject",
                contentUrl: ep.youtubeId.startsWith("TBD_")
                  ? "https://youtube.com/@RajShamani"
                  : `https://www.youtube.com/watch?v=${ep.youtubeId}`,
              },
            };
          }),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Figuring Out — The Podcast | Raj Shamani",
          headline: "Figuring Out — The Podcast | Raj Shamani",
          description: "Figuring Out with Raj Shamani — unfiltered, long-form conversations with India's sharpest founders, leaders, and thinkers.",
          url: "https://rajshamani.life/podcast",
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", name: "Raj Shamani", url: "https://rajshamani.life/" },
          about: { "@type": "PodcastSeries", name: "Figuring Out", url: "https://rajshamani.life/podcast" },
          primaryImageOfPage: {
            "@type": "ImageObject",
            url: "https://rajshamani.life/__l5e/assets-v1/41ab8a52-52fd-4f68-937a-5f4dec00c4d5/og-podcast.jpg",
          },
          image: "https://rajshamani.life/__l5e/assets-v1/41ab8a52-52fd-4f68-937a-5f4dec00c4d5/og-podcast.jpg",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: PODCAST_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Podcast,
});

const PODCAST_FAQS: { q: string; a: string }[] = [
  {
    q: "What is the Figuring Out podcast?",
    a: "Figuring Out is Raj Shamani's long-form interview podcast featuring India's top founders, investors, creators, and cultural voices. It is one of India's most-watched business podcasts and is available on YouTube, Spotify, and Apple Podcasts.",
  },
  {
    q: "Where can I listen to the Raj Shamani podcast?",
    a: "You can watch Figuring Out with Raj Shamani on YouTube at youtube.com/@RajShamani, or listen on Spotify and Apple Podcasts. Full episode summaries, key takeaways, and guest details are available on this page and the individual episode pages.",
  },
  {
    q: "How often are new Figuring Out episodes released?",
    a: "New episodes of Figuring Out are released weekly. Follow the show on YouTube or Spotify to be notified, or subscribe to the Raj Shamani newsletter for a weekly recap.",
  },
  {
    q: "Who has been a guest on Figuring Out with Raj Shamani?",
    a: "Figuring Out has hosted India's leading founders, investors, and cultural voices — including Nikhil Kamath, Kunal Shah, Deepinder Goyal, Ghazal Alagh, Falguni Nayar, and many more. Browse the full episode list above to see every guest.",
  },
  {
    q: "How can I be a guest on the Raj Shamani podcast?",
    a: "Fill out the pitch form on the Be a Guest page. Every application is reviewed by Raj's team. Guests are selected based on the strength of the story, unique lived experience, and fit with the show.",
  },
  {
    q: "Who is Raj Shamani, the podcast host?",
    a: "Raj Shamani is an Indian entrepreneur, angel investor, and author of Build, Don't Talk. He is the director of Shamani Industries and co-founder of House of X. Read his full biography on the About page.",
  },
];


function Podcast() {
  const [query, setQuery] = useState("");
  const [activeTopic, setActiveTopic] = useState("All");

  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    return episodes.filter((ep) => matchesQuery(ep, query) && matchesTopic(ep, activeTopic));
  }, [query, activeTopic]);

  // Reset pagination when filters change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [query, activeTopic]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;


  return (
    <div>
      <PageBreadcrumb current="Podcast" />
      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4">The Podcast</div>
          <div className="grid md:grid-cols-[1fr_auto] gap-8 items-end">
            <h1 className="text-5xl md:text-7xl font-bold">
              Raj Shamani Podcast — Figuring <span className="text-gradient-gold">Out</span>
            </h1>
            <div className="flex gap-3">
              <a href="https://youtube.com/@RajShamani" target="_blank" rel="noopener" className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gold/40 text-sm hover:bg-gold/10">
                <Youtube size={16} /> YouTube
              </a>
              <a href="https://open.spotify.com" target="_blank" rel="noopener" className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-gold/40 text-sm hover:bg-gold/10">
                <Headphones size={16} /> Spotify
              </a>
            </div>
          </div>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl">
            Long-form, unfiltered conversations with the founders, leaders, and
            thinkers shaping India's next decade. New episodes every week.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24">
        <h2 className="text-2xl md:text-3xl font-bold mb-6">Recent Episodes</h2>

        <div className="mb-10 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder="Search by guest, topic, or keyword…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full h-10 pl-9 pr-10 rounded-md border border-input bg-transparent text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {TOPICS.map((topic) => (
              <button
                key={topic.label}
                onClick={() => setActiveTopic(topic.label)}
                className={cn(
                  "inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-medium transition cursor-pointer",
                  activeTopic === topic.label
                    ? "border-gold/60 bg-gold/15 text-gold"
                    : "border-border bg-card text-muted-foreground hover:text-foreground hover:border-gold/30"
                )}
              >
                {topic.label}
              </button>
            ))}
          </div>

          <p className="text-sm text-muted-foreground">
            {filtered.length} episode{filtered.length !== 1 ? "s" : ""} found
          </p>
        </div>

        {filtered.length > 0 ? (
          <>
          <div className="grid md:grid-cols-2 gap-4">
            {visible.map((ep) => (

              <Link
                key={ep.slug}
                to="/podcast/$slug"
                params={{ slug: ep.slug }}
                className="group p-6 rounded-xl bg-card border border-border hover:border-gold/40 transition flex gap-5 items-start"
              >
                <div className="w-14 h-14 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center group-hover:bg-gold group-hover:text-primary-foreground transition shrink-0">
                  <Play size={20} fill="currentColor" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs tracking-widest uppercase text-gold mb-1">{ep.n} · {ep.d}</div>
                  <h3 className="font-bold text-lg leading-tight mb-1">{ep.t}</h3>
                  <p className="text-sm text-muted-foreground">with {ep.g}</p>
                </div>
              </Link>
            ))}
          </div>
          {hasMore && (
            <div className="mt-10 flex justify-center">
              <button
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md border border-gold/40 text-sm font-medium hover:bg-gold/10 cursor-pointer"
              >
                Load more episodes ({filtered.length - visibleCount} remaining)
              </button>
            </div>
          )}
          </>

        ) : (
          <div className="text-center py-16">
            <p className="text-muted-foreground mb-2">No episodes match your search.</p>
            <button
              onClick={() => { setQuery(""); setActiveTopic("All"); }}
              className="text-gold hover:underline text-sm cursor-pointer"
            >
              Clear filters
            </button>
          </div>
        )}

        <div className="mt-16 text-center">
          <Link
            to="/guest"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-md bg-gold text-primary-foreground font-medium hover:opacity-90"
          >
            Want to be a guest? Pitch yourself →
          </Link>
        </div>
      </section>

      {/* Podcast FAQ — targets "raj shamani podcast", "figuring out podcast" queries */}
      <section className="border-t border-border bg-noir/20">
        <div className="mx-auto max-w-4xl px-6 py-24">
          <div className="text-[11px] tracking-[0.2em] uppercase text-gold mb-4">Podcast FAQ</div>
          <h2 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mb-12">
            About the Figuring Out podcast
          </h2>
          <div className="divide-y divide-border border-y border-border">
            {PODCAST_FAQS.map((f) => (
              <details key={f.q} className="group py-6">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <h3 className="font-display text-lg md:text-xl font-semibold text-foreground">
                    {f.q}
                  </h3>
                  <span className="text-gold text-2xl leading-none mt-1 group-open:rotate-45 transition">+</span>
                </summary>
                <p className="mt-4 text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>

          <div className="mt-10 text-sm text-muted-foreground leading-relaxed">
            More about the host on the{" "}
            <Link to="/about" className="text-gold underline underline-offset-4">Raj Shamani biography</Link> page,
            or read the full{" "}
            <Link to="/faq" className="text-gold underline underline-offset-4">Raj Shamani FAQ</Link> covering age,
            net worth, education, and family. See Raj's{" "}
            <Link to="/ventures" className="text-gold underline underline-offset-4">ventures &amp; investments</Link>{" "}
            and his bestselling book{" "}
            <Link to="/build-dont-talk" className="text-gold underline underline-offset-4">Build, Don't Talk</Link>.
          </div>
        </div>
      </section>

      <KeywordHubLinks />

    </div>
  );
}
